import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { dataRevision } from './invalidation';

export interface QueryState<T> {
  data: T | undefined;
  error: Error | null;
  loading: boolean;
  /** True while re-fetching with previous data still displayed. */
  refreshing: boolean;
  reload: () => void;
}

/**
 * Minimal async data hook. Re-runs when `deps` change or after a mutation
 * calls `invalidate()`, keeps previous data while refreshing and ignores
 * stale responses.
 */
export function useQuery<T>(fetcher: () => Promise<T>, deps: readonly unknown[]): QueryState<T> {
  const revision = useSyncExternalStore(dataRevision.subscribe, dataRevision.get);
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [manual, setManual] = useState(0);
  const requestId = useRef(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    fetcherRef
      .current()
      .then((result) => {
        if (id === requestId.current) setData(result);
      })
      .catch((err: unknown) => {
        if (id === requestId.current) setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, revision, manual]);

  const reload = useCallback(() => setManual((n) => n + 1), []);

  return { data, error, loading: loading && data === undefined, refreshing: loading && data !== undefined, reload };
}
